import { useCallback } from 'react';

import { createEntityAdapter, createSlice } from '@reduxjs/toolkit';
import utcToZonedTime from 'date-fns-tz/utcToZonedTime';
import parseISO from 'date-fns/parseISO';

import { useAppDispatch, useAppSelector } from '@shared/hooks';
import type { AvailablePeriod } from '@shared/types/available-periods';

const availablePeriodAdapter = createEntityAdapter<AvailablePeriod>();

const availablePeriodsSlice = createSlice({
	name: 'availablePeriods',
	initialState: availablePeriodAdapter.getInitialState(),
	reducers: {
		availablePeriodAdd: availablePeriodAdapter.addOne,
		availablePeriodsAdd: availablePeriodAdapter.addMany,
		availablePeriodsSet: availablePeriodAdapter.setAll,
		availablePeriodSet: availablePeriodAdapter.setOne,
		availablePeriodRemove: availablePeriodAdapter.removeOne,
	},
});

export const availablePeriodsReducer = availablePeriodsSlice.reducer;
export const availablePeriodsActions = availablePeriodsSlice.actions;

export const availablePeriodsSelectors = availablePeriodAdapter.getSelectors();

export const useAvailablePeriods = () => {
	const dispatch = useAppDispatch();

	const availablePeriods = useAppSelector((state) =>
		availablePeriodsSelectors.selectAll(state.availablePeriods)
	);

	const addAvailablePeriods = (availablePeriods: AvailablePeriod[]) =>
		dispatch(availablePeriodsActions.availablePeriodsAdd(availablePeriods));

	const addAvailablePeriod = (availablePeriod: AvailablePeriod) =>
		dispatch(availablePeriodsActions.availablePeriodAdd(availablePeriod));

	const setAvailablePeriods = (availablePeriods: AvailablePeriod[]) =>
		dispatch(availablePeriodsActions.availablePeriodsSet(availablePeriods));

	const setAvailablePeriod = (availablePeriod: AvailablePeriod) =>
		dispatch(availablePeriodsActions.availablePeriodSet(availablePeriod));

	const removeAvailablePeriod = (id: number) =>
		dispatch(availablePeriodsActions.availablePeriodRemove(id));

	const findAvailable = useCallback(
		(session: { start: Date; end: Date }, tz: string): AvailablePeriod | undefined => {
			if (availablePeriods.length === 0) return;

			const { start, end } = session;
			return availablePeriods.find((available) => {
				return (
					utcToZonedTime(parseISO(available.start_time), tz) <= start &&
					utcToZonedTime(parseISO(available.end_time), tz) >= end
				);
			});
		},
		[availablePeriods]
	);

	return {
		availablePeriods,
		addAvailablePeriods,
		addAvailablePeriod,
		setAvailablePeriods,
		setAvailablePeriod,
		removeAvailablePeriod,
		findAvailable,
	};
};

export const useAvailablePeriod = (id: number) => {
	const availablePeriod = useAppSelector((state) =>
		availablePeriodsSelectors.selectById(state.availablePeriods, id)
	);

	return {
		availablePeriod,
	};
};
